import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { app } from '../firebase';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';

interface UserDoc {
  uid: string;
  email: string;
  role: 'member' | 'admin';
  username?: string;
  mainCharacterName?: string;
}

const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [newRole, setNewRole] = useState<'member' | 'admin'>('member');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDoc | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteUser, setDeleteUser] = useState<UserDoc | null>(null);
  const [editingMainCharacterName, setEditingMainCharacterName] = useState<string | null>(null);
  const [mainCharacterNameValue, setMainCharacterNameValue] = useState<string>('');
  const [savingMainCharacterName, setSavingMainCharacterName] = useState<string | null>(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    const fetchUsers = async () => {
      setLoading(true);
      const db = getFirestore(app);
      const querySnapshot = await getDocs(collection(db, 'users'));
      const userList: UserDoc[] = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          uid: docSnap.id,
          email: data.email || '',
          role: data.role || 'member',
          username: data.username || '',
          mainCharacterName: data.mainCharacterName || '',
        };
      });
      setUsers(userList);
      setLoading(false);
    };
    fetchUsers();
  }, [user, navigate]);

  const handleRoleChange = (userDoc: UserDoc, role: 'member' | 'admin') => {
    setSelectedUser(userDoc);
    setNewRole(role);
    setConfirmOpen(true);
  };

  const confirmRoleChange = async () => {
    if (!selectedUser) return;
    const db = getFirestore(app);
    await updateDoc(doc(db, 'users', selectedUser.uid), { role: newRole });
    setUsers(users => users.map(u => u.uid === selectedUser.uid ? { ...u, role: newRole } : u));
    setConfirmOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = (userDoc: UserDoc) => {
    setDeleteUser(userDoc);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!deleteUser) return;
    const db = getFirestore(app);
    // Delete from Firestore
    await deleteDoc(doc(db, 'users', deleteUser.uid));
    // Get admin's ID token
    const currentUser = getAuth().currentUser;
    const token = currentUser ? await currentUser.getIdToken() : null;
    // Call backend to delete from Auth
    await fetch('/api/deleteUser', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ uid: deleteUser.uid })
    });
    setUsers(users => users.filter(u => u.uid !== deleteUser.uid));
    setDeleteConfirmOpen(false);
    setDeleteUser(null);
  };

  const handleMainCharacterNameEdit = (userDoc: UserDoc) => {
    setEditingMainCharacterName(userDoc.uid);
    setMainCharacterNameValue(userDoc.mainCharacterName || '');
  };

  const handleMainCharacterNameSave = async (userDoc: UserDoc) => {
    if (!editingMainCharacterName) return;
    setSavingMainCharacterName(userDoc.uid);
    try {
      const db = getFirestore(app);
      await updateDoc(doc(db, 'users', userDoc.uid), { 
        mainCharacterName: mainCharacterNameValue.trim() || null 
      });
      setUsers(users => users.map(u => 
        u.uid === userDoc.uid 
          ? { ...u, mainCharacterName: mainCharacterNameValue.trim() || '' } 
          : u
      ));
      setEditingMainCharacterName(null);
      setMainCharacterNameValue('');
    } catch (err) {
      console.error('Failed to update main character name:', err);
    } finally {
      setSavingMainCharacterName(null);
    }
  };

  const handleMainCharacterNameCancel = () => {
    setEditingMainCharacterName(null);
    setMainCharacterNameValue('');
  };

  const filteredUsers = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.username && u.username.toLowerCase().includes(search.toLowerCase())) ||
    (u.mainCharacterName && u.mainCharacterName.toLowerCase().includes(search.toLowerCase())) ||
    u.uid.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page-container bg-surface">
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <h1 className="text-4xl font-bold mb-8 text-center">
          <span className="gradient-text">User Management</span>
        </h1>
        <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <input
            type="text"
            placeholder="Search by email, username, or character name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full sm:w-80 p-2 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400"
          />
        </div>
        {loading ? (
          <div className="text-center text-text-secondary">Loading users...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-surface rounded-xl overflow-hidden">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Username</th>
                  <th className="px-4 py-2 text-left">Main Character Name</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.uid} className="border-b border-border hover:bg-surface-light transition">
                    <td className="px-4 py-2 break-all">{u.email}</td>
                    <td className="px-4 py-2">
                      {u.username || <span className="text-text-secondary italic">Not set</span>}
                    </td>
                    <td className="px-4 py-2">
                      {editingMainCharacterName === u.uid ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={mainCharacterNameValue}
                            onChange={e => setMainCharacterNameValue(e.target.value)}
                            className="flex-1 p-1 rounded bg-surface-light border border-border focus:outline-none focus:ring-2 focus:ring-cyan-400 text-sm"
                            placeholder="Enter character name"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleMainCharacterNameSave(u);
                              } else if (e.key === 'Escape') {
                                handleMainCharacterNameCancel();
                              }
                            }}
                          />
                          <button
                            onClick={() => handleMainCharacterNameSave(u)}
                            className="px-2 py-1 bg-cyan-600 text-white rounded hover:bg-cyan-700 text-xs"
                            disabled={savingMainCharacterName === u.uid}
                          >
                            {savingMainCharacterName === u.uid ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleMainCharacterNameCancel}
                            className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-xs"
                            disabled={savingMainCharacterName === u.uid}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={u.mainCharacterName ? '' : 'text-text-secondary italic'}>
                            {u.mainCharacterName || 'Not set'}
                          </span>
                          <button
                            onClick={() => handleMainCharacterNameEdit(u)}
                            className="text-cyan-400 hover:text-cyan-300 text-xs"
                            title="Edit main character name"
                          >
                            ✏️
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 capitalize">{u.role}</td>
                    <td className="px-4 py-2">
                      <select
                        value={u.role}
                        onChange={e => handleRoleChange(u, e.target.value as 'member' | 'admin')}
                        className="p-1 rounded border border-border bg-surface-light"
                        disabled={u.uid === user?.uid}
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                      {u.uid !== user?.uid && (
                        <button
                          className="ml-2 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                          onClick={() => handleDeleteUser(u)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Confirmation Dialog */}
        {confirmOpen && selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-surface p-8 rounded-xl max-w-sm w-full relative">
              <h2 className="text-xl font-bold mb-4 text-center">Confirm Role Change</h2>
              <p className="mb-6 text-center">
                Are you sure you want to change <span className="font-semibold text-cyan-400">{selectedUser.email}</span>'s role to <span className="font-semibold text-cyan-400">{newRole}</span>?
              </p>
              <div className="flex justify-center gap-4">
                <button
                  className="px-6 py-2 bg-cyan-500 text-white rounded hover:bg-cyan-600 transition"
                  onClick={confirmRoleChange}
                >
                  Yes, Change Role
                </button>
                <button
                  className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
                  onClick={() => setConfirmOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Delete Confirmation Dialog */}
        {deleteConfirmOpen && deleteUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-surface p-8 rounded-xl max-w-sm w-full relative">
              <h2 className="text-xl font-bold mb-4 text-center">Confirm Delete User</h2>
              <p className="mb-6 text-center">
                Are you sure you want to <span className="font-semibold text-red-400">permanently delete</span> <span className="font-semibold text-cyan-400">{deleteUser.email}</span>?
                <br/>This cannot be undone.
              </p>
              <div className="flex justify-center gap-4">
                <button
                  className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                  onClick={confirmDeleteUser}
                >
                  Yes, Delete User
                </button>
                <button
                  className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
                  onClick={() => setDeleteConfirmOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement; 